import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star, Trophy, Sparkles, ChevronRight } from 'lucide-react';
import {
  loadGamificationData,
  calculateLevel,
  calculateProgress,
  getAllBadgesWithStatus,
  BADGES,
} from '../services/studentGamification';

/**
 * Student Level Badge - Shows near title
 */
export function StudentLevelBadge({ className = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(loadGamificationData());
  }, []);

  if (!data) return null;

  const level = calculateLevel(data.totalXP);
  const progress = calculateProgress(data.totalXP);

  return (
    <div className={`student-level-badge ${className}`} data-testid="student-level-badge">
      <div className="level-pill">
        <Star size={12} className="level-icon" />
        <span className="level-text">
          {isRTL ? `المستوى ${level}` : `Level ${level}`}
        </span>
      </div>
      <div className="level-progress-bar">
        <div 
          className="level-progress-fill" 
          style={{ width: `${progress.percent}%` }}
        />
      </div>
      <span className="level-progress-text">
        {isRTL 
          ? `${progress.current} / ${progress.needed} نقطة للمستوى التالي`
          : `${progress.current} / ${progress.needed} XP to next level`
        }
      </span>

      <style>{`
        .student-level-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .level-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.625rem;
          background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
          border-radius: 999px;
          font-size: 0.6875rem;
          font-weight: 700;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .level-icon {
          flex-shrink: 0;
        }

        .level-progress-bar {
          width: 100%;
          max-width: 120px;
          height: 4px;
          background: rgba(59, 130, 246, 0.15);
          border-radius: 2px;
          overflow: hidden;
        }

        .level-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3B82F6 0%, #6366F1 100%);
          border-radius: 2px;
          transition: width 300ms ease;
        }

        .level-progress-text {
          font-size: 0.625rem;
          color: rgba(59, 130, 246, 0.7);
          font-weight: 500;
        }

        html.dark .level-progress-text {
          color: rgba(147, 197, 253, 0.7);
        }
      `}</style>
    </div>
  );
}

/**
 * Badges Row - Shows earned badges as chips
 */
export function StudentBadgesRow({ onBadgeClick, className = '' }) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const lang = isRTL ? 'ar' : 'en';
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    setBadges(getAllBadgesWithStatus());
  }, []);

  const unlockedBadges = badges.filter(b => b.unlocked);
  const lockedCount = badges.filter(b => !b.unlocked).length;

  if (unlockedBadges.length === 0) return null;

  return (
    <div className={`student-badges-row ${className}`} data-testid="student-badges-row">
      <div className="badges-scroll">
        {unlockedBadges.map((badge) => (
          <button
            key={badge.id}
            type="button"
            className="badge-chip unlocked"
            onClick={() => onBadgeClick?.(badge)}
            data-testid={`badge-${badge.id}`}
          >
            <span className="badge-emoji">{badge.emoji}</span>
            <span className="badge-name">{badge.name[lang]}</span>
          </button>
        ))}
        {lockedCount > 0 && (
          <button
            type="button"
            className="badge-chip locked-hint"
            onClick={() => onBadgeClick?.({ showAll: true })}
          >
            <span className="badge-emoji">🔒</span>
            <span className="badge-name">+{lockedCount}</span>
          </button>
        )}
      </div>

      <style>{`
        .student-badges-row {
          padding: 0.5rem 1rem;
          background: rgba(59, 130, 246, 0.04);
          border-bottom: 1px solid rgba(59, 130, 246, 0.08);
        }

        html.dark .student-badges-row {
          background: rgba(59, 130, 246, 0.08);
          border-color: rgba(59, 130, 246, 0.15);
        }

        .badges-scroll {
          display: flex;
          gap: 0.5rem;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .badges-scroll::-webkit-scrollbar {
          display: none;
        }

        .badge-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.625rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 150ms ease;
          border: none;
          flex-shrink: 0;
        }

        .badge-chip.unlocked {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .badge-chip.unlocked:hover {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%);
          transform: translateY(-1px);
        }

        html.dark .badge-chip.unlocked {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%);
          color: #93c5fd;
          border-color: rgba(59, 130, 246, 0.3);
        }

        .badge-chip.locked-hint {
          background: rgba(148, 163, 184, 0.1);
          color: #94a3b8;
          border: 1px dashed rgba(148, 163, 184, 0.3);
        }

        .badge-emoji {
          font-size: 0.875rem;
        }

        .badge-name {
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}

/**
 * Badge Details Modal
 */
export function BadgeModal({ badge, onClose, lang = 'en' }) {
  const { t } = useTranslation();
  const [allBadges, setAllBadges] = useState([]);

  useEffect(() => {
    if (badge?.showAll) {
      setAllBadges(getAllBadgesWithStatus());
    }
  }, [badge]);

  if (!badge) return null;

  const isShowAll = badge.showAll;
  const isRTL = lang === 'ar';

  return (
    <div className="badge-modal-overlay" onClick={onClose}>
      <div 
        className="badge-modal" 
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <button type="button" className="badge-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        {isShowAll ? (
          <>
            <div className="badge-modal-header">
              <Trophy size={24} className="header-icon" />
              <h3>{t('studgami.allBadges')}</h3>
            </div>
            <div className="badges-grid">
              {allBadges.map((b) => (
                <div 
                  key={b.id} 
                  className={`badge-card ${b.unlocked ? 'unlocked' : 'locked'}`}
                >
                  <span className="badge-card-emoji">{b.emoji}</span>
                  <span className="badge-card-name">{b.name[lang]}</span>
                  <span className="badge-card-desc">{b.description[lang]}</span>
                  {!b.unlocked && (
                    <span className="badge-card-progress">
                      {b.progress} / {b.requirement.count}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="badge-detail">
            <span className="badge-detail-emoji">{badge.emoji}</span>
            <h3 className="badge-detail-name">{badge.name[lang]}</h3>
            <p className="badge-detail-desc">{badge.description[lang]}</p>
            <div className="badge-detail-status">
              <Sparkles size={14} />
              <span>{t('studgami.unlocked')}</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .badge-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .badge-modal {
          position: relative;
          background: white;
          border-radius: 1.25rem;
          padding: 1.5rem;
          max-width: 360px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        html.dark .badge-modal {
          background: #1e293b;
        }

        .badge-modal-close {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.05);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 150ms ease;
        }

        [dir="rtl"] .badge-modal-close {
          right: auto;
          left: 0.75rem;
        }

        .badge-modal-close:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .badge-modal-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .badge-modal-header .header-icon {
          color: #3b82f6;
        }

        .badge-modal-header h3 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e3a8a;
          margin: 0;
        }

        html.dark .badge-modal-header h3 {
          color: #93c5fd;
        }

        .badges-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .badge-card {
          padding: 1rem;
          border-radius: 0.75rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          transition: all 150ms ease;
        }

        .badge-card.unlocked {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .badge-card.locked {
          background: rgba(148, 163, 184, 0.08);
          border: 1px dashed rgba(148, 163, 184, 0.2);
          opacity: 0.7;
        }

        .badge-card-emoji {
          font-size: 1.5rem;
        }

        .badge-card.locked .badge-card-emoji {
          filter: grayscale(1);
        }

        .badge-card-name {
          font-size: 0.75rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        html.dark .badge-card-name {
          color: #93c5fd;
        }

        .badge-card-desc {
          font-size: 0.625rem;
          color: #64748b;
        }

        .badge-card-progress {
          font-size: 0.625rem;
          color: #94a3b8;
          font-weight: 600;
        }

        .badge-detail {
          text-align: center;
        }

        .badge-detail-emoji {
          font-size: 3rem;
          display: block;
          margin-bottom: 0.75rem;
        }

        .badge-detail-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e3a8a;
          margin: 0 0 0.25rem;
        }

        html.dark .badge-detail-name {
          color: #93c5fd;
        }

        .badge-detail-desc {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0 0 1rem;
        }

        .badge-detail-status {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
          color: white;
          border-radius: 999px;
          font-size: 0.8125rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

/**
 * XP Toast Notification
 */
export function XPToast({ xp, message, onClose }) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');

  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="xp-toast" dir={isRTL ? 'rtl' : 'ltr'}>
      <Sparkles size={16} className="xp-toast-icon" />
      <span className="xp-toast-text">
        +{xp} XP {message && `• ${message}`}
      </span>

      <style>{`
        .xp-toast {
          position: fixed;
          top: 80px;
          left: 50%;
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
          color: white;
          border-radius: 999px;
          font-size: 0.875rem;
          font-weight: 600;
          box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
          z-index: 1001;
          animation: xpToastIn 300ms ease;
        }

        @keyframes xpToastIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        .xp-toast-icon {
          flex-shrink: 0;
          color: #fbbf24;
        }
      `}</style>
    </div>
  );
}

/**
 * Level Up Modal
 */
export function LevelUpModal({ level, onClose }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');

  return (
    <div className="levelup-modal-overlay" onClick={onClose}>
      <div className="levelup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="levelup-confetti">🎉</div>
        <h2 className="levelup-title">
          {t('studgami.levelUp')}
        </h2>
        <div className="levelup-level">
          <Star size={24} />
          <span>{isRTL ? `المستوى ${level}` : `Level ${level}`}</span>
        </div>
        <p className="levelup-message">
          {t('studgami.greatJobKeepGoing')
          }
        </p>
        <button type="button" className="levelup-btn" onClick={onClose}>
          {t('onborole.continue')}
        </button>
      </div>

      <style>{`
        .levelup-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1002;
          padding: 1rem;
        }

        .levelup-modal {
          background: white;
          border-radius: 1.5rem;
          padding: 2rem;
          text-align: center;
          max-width: 300px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(59, 130, 246, 0.3);
          animation: levelUpBounce 500ms ease;
        }

        html.dark .levelup-modal {
          background: #1e293b;
        }

        @keyframes levelUpBounce {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        .levelup-confetti {
          font-size: 3rem;
          margin-bottom: 0.5rem;
          animation: confettiBounce 600ms ease infinite alternate;
        }

        @keyframes confettiBounce {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }

        .levelup-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1e3a8a;
          margin: 0 0 0.75rem;
        }

        html.dark .levelup-title {
          color: #93c5fd;
        }

        .levelup-level {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
          color: white;
          border-radius: 999px;
          font-size: 1.125rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
        }

        .levelup-message {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0 0 1.25rem;
        }

        .levelup-btn {
          width: 100%;
          padding: 0.875rem;
          background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 150ms ease;
        }

        .levelup-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
        }

        .levelup-btn:active {
          transform: scale(0.97);
        }
      `}</style>
    </div>
  );
}

/**
 * Streak Display (soft, gentle)
 */
export function StudentStreak({ className = '' }) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(loadGamificationData());
  }, []);

  if (!data || data.streak.current < 2) return null;

  return (
    <div className={`student-streak ${className}`}>
      <span className="streak-icon">🔥</span>
      <span className="streak-text">
        {isRTL 
          ? `${data.streak.current} أيام متتالية`
          : `${data.streak.current} day streak`
        }
      </span>

      <style>{`
        .student-streak {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.625rem;
          background: rgba(251, 191, 36, 0.12);
          border-radius: 999px;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #d97706;
        }

        html.dark .student-streak {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }

        .streak-icon {
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
}

export default {
  StudentLevelBadge,
  StudentBadgesRow,
  BadgeModal,
  XPToast,
  LevelUpModal,
  StudentStreak,
};
